'use client';

import { useRef, useTransition } from 'react';
import Card, { CardVariant } from '@pixum/card';
import { Alignment } from '@pixum/types';
import { useImageStateStore } from 'stores/express-designer';
import { getImageAlignment, fillCreationWithImage, openImageUploadPicker } from '../../../utils';
import { ButtonWithAddCreationProductToCart, CreationThumbnail } from 'components';
import { addExpressProductToCartAction } from '../../../actions/addExpressProductToCartAction';
import { logger, trackAddProductToCart } from 'utils';
import type { ExpressDesignerProductNode } from 'data-layer';
import { EXPRESS_DESIGNER_EVENT_ACTION } from '../../../constants';
import styles from './ProductCard.module.css';

interface ProductCardProps {
  product: ExpressDesignerProductNode;
  constants: Record<string, string>;
  initialSelectedImageId: string | undefined;
}

export function ProductCard(props: ProductCardProps) {
  const { product, constants, initialSelectedImageId } = props;

  const storeImages = useImageStateStore(state => state.images);
  const storeSelectedImage = useImageStateStore(state => state.selectedImage);
  const previouslyAddedProductIds = useImageStateStore(state => state.previouslyAddedProductIds);
  const setProductAdded = useImageStateStore(state => state.setProductAdded);
  const setCartError = useImageStateStore(state => state.setCartError);

  const [isBusy, startTransition] = useTransition();
  /*
   * Button click bubbles to the card; isBusy lags a render,
   * so a ref blocks the duplicate call.
   */
  const isAddingRef = useRef(false);

  const selectedImage =
    storeSelectedImage ?? storeImages.find(img => img.uuid === initialSelectedImageId) ?? storeImages[0];

  const productId = String(product.productId ?? '');
  const isEffectivelyAdded = previouslyAddedProductIds.has(productId);
  const alignment = getImageAlignment(selectedImage);

  // Square images reuse the landscape creation
  const alignmentKey = alignment === Alignment.SQUARE ? Alignment.LANDSCAPE : alignment;
  const { rawCreation, normalizedCreation } = product.editorRotateAllowed
    ? {
        rawCreation: product.creations[alignmentKey],
        normalizedCreation: product.normalizedCreationByAlignment[alignmentKey]
      }
    : { rawCreation: product.creation, normalizedCreation: product.normalizedCreation };

  const displayCreation =
    selectedImage && rawCreation ? fillCreationWithImage(rawCreation, selectedImage) : rawCreation;

  const handleAddToCart = () => {
    const isAdding = isBusy || isAddingRef.current;

    if (!selectedImage?.uuid) return;

    if (isEffectivelyAdded || isAdding) return;

    isAddingRef.current = true;
    setCartError(false);
    startTransition(async () => {
      try {
        const result = await addExpressProductToCartAction({
          productId,
          imageId: selectedImage.uuid,
          imageWidth: selectedImage.rotatedWidth ?? selectedImage.width,
          imageHeight: selectedImage.rotatedHeight ?? selectedImage.height,
          alignment,
          normalizedCreation
        });

        if (result.success) {
          trackAddProductToCart(EXPRESS_DESIGNER_EVENT_ACTION, product.title);
          setProductAdded({ productId, articleId: result.articleId, creationId: result.creationId });
        } else {
          setCartError(true);
        }
      } catch (error) {
        logger.error({ error, productId }, 'addExpressProductToCartAction rejected');

        setCartError(true);
      } finally {
        isAddingRef.current = false;
      }
    });
  };

  const media = (
    <div className={styles.thumbnailWrapper}>
      <CreationThumbnail creation={displayCreation} previewUrl="" alt={product.title} />
    </div>
  );

  return (
    <Card
      id={`express-product-${productId}`}
      variant={CardVariant.ProductCard}
      className={styles.card}
      media={media}
      headline={product.title}
      supportingText={product.priceFormats?.withAsterisk}
      onClick={selectedImage?.uuid ? handleAddToCart : openImageUploadPicker}
      primaryAction={{
        children: (
          <ButtonWithAddCreationProductToCart
            isBusy={isBusy}
            isEffectivelyAdded={isEffectivelyAdded}
            isDisabled={!selectedImage?.uuid}
            constants={constants}
            onClick={handleAddToCart}
          />
        )
      }}
    />
  );
}
